import React, { useState, useEffect } from 'react';
import { FormCheckbox } from '../../../FormCheckbox';

/**
 * Step 3: Acknowledgement/Acceptance
 * 
 * Purpose: Get explicit user confirmation before document submission
 * 
 * Modes:
 * - ACKNOWLEDGE: User confirms via checkbox they acknowledge/accept
 * - ACCEPT: User confirms they understand they are signing the document
 * 
 * Key Features:
 * - Explicit confirmation required
 * - Display key document details for final confirmation
 * - Cannot proceed without confirmation
 * - Different wording based on acceptance mode
 */
const Step3_Acknowledgement = ({
  wizardData,
  template,
  onDataChange,
  onValidationChange
}) => {
  const [acknowledged, setAcknowledged] = useState(wizardData.acknowledged || false);

  // Determine acceptance mode
  const isSigningMode = template?.acceptance_mode === 'accept';
  
  // Validate acknowledgment
  useEffect(() => {
    onValidationChange(acknowledged === true);
  }, [acknowledged, onValidationChange]);

  const handleAcknowledgmentChange = (e) => {
    const isChecked = e.target.checked;
    setAcknowledged(isChecked);
    onDataChange({
      ...wizardData,
      acknowledged: isChecked
    });
  };

  return (
    <div className="space-y-6">
      {/* Document Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4 text-gray-900">Document Summary</h3>
        
        <div className="space-y-3 text-sm">
          {template && (
            <>
              <div>
                <span className="text-gray-600">Document Template:</span>
                <p className="font-semibold text-gray-900">{template.title}</p>
              </div>
              
              {template.description && (
                <div>
                  <span className="text-gray-600">Description:</span>
                  <p className="text-gray-700 mt-1">{template.description}</p>
                </div>
              )}
            </>
          )}

          {wizardData.templateName && (
            <div>
              <span className="text-gray-600">Type:</span>
              <p className="font-semibold text-gray-900">{wizardData.templateName}</p>
            </div>
          )}

          {wizardData.clubName && (
            <div>
              <span className="text-gray-600">Club:</span>
              <p className="font-semibold text-gray-900">{wizardData.clubName}</p>
            </div>
          )}

          {wizardData.teamName && (
            <div>
              <span className="text-gray-600">Team:</span>
              <p className="font-semibold text-gray-900">{wizardData.teamName}</p>
            </div>
          )}
        </div>
      </div>

      {/* Legal Compliance Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        {isSigningMode ? (
          <p className="text-sm text-blue-900">
            <span className="font-semibold">⚠️ Important:</span> By signing this document, you are providing your legal signature. 
            This submission will be permanently recorded. Please review all information carefully before signing.
          </p>
        ) : (
          <p className="text-sm text-blue-900">
            <span className="font-semibold">⚠️ Important:</span> Once you acknowledge this document, it will be converted to a PDF and stored permanently. 
            Please review all information carefully before accepting.
          </p>
        )}
      </div>

      {/* Acknowledgment Checkbox */}
      <div className="border-2 border-gray-200 rounded-lg p-6 bg-white">
        {isSigningMode ? (
          <FormCheckbox
            label="I certify that I have read and understand this document, and I hereby sign it"
            name="acknowledged"
            checked={acknowledged}
            onChange={handleAcknowledgmentChange}
            required
            error={!acknowledged && acknowledged !== null ? 'You must certify before signing' : null}
          />
        ) : (
          <FormCheckbox
            label="I acknowledge and accept the above information"
            name="acknowledged"
            checked={acknowledged}
            onChange={handleAcknowledgmentChange}
            required
            error={!acknowledged && acknowledged !== null ? 'You must acknowledge before proceeding' : null}
          />
        )}
        
        <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="text-xs text-gray-700 leading-relaxed">
            {isSigningMode ? (
              <>
                <strong>By signing, you confirm that:</strong>
                <ul className="mt-2 space-y-1 ml-3">
                  <li>✓ You have read and fully understand this document</li>
                  <li>✓ All information you provided is accurate and complete</li>
                  <li>✓ You are authorized to sign this document</li>
                  <li>✓ Your signature constitutes a legal commitment</li>
                  <li>✓ This signature will be permanently recorded</li>
                </ul>
              </>
            ) : (
              <>
                <strong>By checking this box, you confirm that:</strong>
                <ul className="mt-2 space-y-1 ml-3">
                  <li>✓ You have read and reviewed all document information</li>
                  <li>✓ All provided information is accurate and complete</li>
                  <li>✓ You are authorized to acknowledge this document</li>
                  <li>✓ You understand this is a binding submission</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Additional Confirmation Message */}
      {acknowledged && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            ✓ {isSigningMode ? 'Thank you for signing. Click "Next" to finalize the submission.' : 'Thank you for confirming. Click "Next" to proceed to document submission.'}
          </p>
        </div>
      )}
    </div>
  );
};

export { Step3_Acknowledgement };
